import React from 'react';
import { Shield } from 'lucide-react';
import { formatMvrDate } from '../services/mvrService';

/** Lien 1-4 fields as the API returns them on a title record. */
export interface AstsPublicLienFields {
  lienholderName?: string;
  lienholderAddress?: string;
  lienholderCity?: string;
  lienholderState?: string;
  lienholderZip?: string;
  lienDate?: string;
  lien2Name?: string;
  lien2Address?: string;
  lien2City?: string;
  lien2State?: string;
  lien2Zip?: string;
  lien2Date?: string;
  lien3Name?: string;
  lien3Address?: string;
  lien3City?: string;
  lien3State?: string;
  lien3Zip?: string;
  lien3Date?: string;
  lien4Name?: string;
  lien4Address?: string;
  lien4City?: string;
  lien4State?: string;
  lien4Zip?: string;
  lien4Date?: string;
}

interface Lien {
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  date?: string;
}

const DataField: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <div className="text-xs text-[#64748b] mb-0.5">{label}</div>
      <div className="font-medium text-[#0f172a]">{value}</div>
    </div>
  );
};

/** "Street, City, ST 12345", skipping whatever parts are missing. */
const formatAddress = ({ address, city, state, zip }: Lien): string => {
  const cityLine = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [address, cityLine].filter(Boolean).join(', ');
};

/**
 * Every lienholder on a title, one section each. A state can report up to four (Florida sends a
 * second lien as Lien 2); a lien only shows when it has a name.
 */
const AstsPublicLienholders: React.FC<{ title: AstsPublicLienFields }> = ({ title }) => {
  const liens: Lien[] = [
    { name: title.lienholderName, address: title.lienholderAddress, city: title.lienholderCity, state: title.lienholderState, zip: title.lienholderZip, date: title.lienDate },
    { name: title.lien2Name, address: title.lien2Address, city: title.lien2City, state: title.lien2State, zip: title.lien2Zip, date: title.lien2Date },
    { name: title.lien3Name, address: title.lien3Address, city: title.lien3City, state: title.lien3State, zip: title.lien3Zip, date: title.lien3Date },
    { name: title.lien4Name, address: title.lien4Address, city: title.lien4City, state: title.lien4State, zip: title.lien4Zip, date: title.lien4Date },
  ].filter(l => l.name);
  if (liens.length === 0) return null;

  return (
    <>
      {liens.map((lien, i) => (
        <div key={i}>
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-[#64748b]" />
            <span className="text-sm font-semibold text-[#374151]">
              {liens.length > 1 ? `Lienholder ${i + 1}` : 'Lienholder'}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2">
              <DataField label="Name" value={lien.name} />
            </div>
            <DataField label="Lien Date" value={formatMvrDate(lien.date)} />
            {formatAddress(lien) && (
              <div className="col-span-2">
                <DataField label="Address" value={formatAddress(lien)} />
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export default AstsPublicLienholders;
